import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, HostBinding, Input, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPaginationModule } from 'ng-zorro-antd/pagination';
import { NzPopconfirmModule } from 'ng-zorro-antd/popconfirm';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

export interface TenantPublicIpItem {
  id: string;
  tenantId: string;
  publicIp: string;
  normalizedPublicIp?: string;
  ipVersion?: string;
  status?: string;
  creatorId?: string;
  creatorName?: string;
  remark?: string;
  createTime?: number | string;
}

@Component({
  selector: 'tenant-ip-range',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzTableModule,
    NzButtonModule,
    NzIconModule,
    NzInputModule,
    NzModalModule,
    NzPopconfirmModule,
    NzCheckboxModule,
    NzPaginationModule,
    NzSpinModule,
    NzToolTipModule,
    NzTagModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './tenant-ip-range.component.html',
  styleUrls: ['./tenant-ip-range.component.less']
})
export class TenantIpRangeComponent implements OnInit {
  @Input() tenantId: string = '';
  @Input() embeddedMode = false;

  @HostBinding('class.embedded-mode')
  get isEmbedded(): boolean {
    return this.embeddedMode;
  }

  listOfData: TenantPublicIpItem[] = [];
  loading = false;
  total = 0;
  pageSize = 20;
  pageIndex = 1;
  searchKeyword = '';
  order = 'desc';

  // 多選狀態
  selectedIds = new Set<string>();
  isAllCurrentPageSelected = false;
  isIndeterminate = false;

  // 批量新增 Modal
  batchAddVisible = false;
  batchAddLoading = false;
  batchAddForm!: FormGroup;

  private readonly http = inject(HttpService);
  private readonly message = inject(NzMessageService);
  private readonly modal = inject(NzModalService);
  private readonly fb = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);

  ngOnInit(): void {
    this.initForm();
    if (this.tenantId) {
      this.loadData();
    }
  }

  initForm(): void {
    this.batchAddForm = this.fb.group({
      ips: ['', [Validators.required]],
      remark: ['', [Validators.maxLength(100)]]
    });
  }

  get displayedListOfData(): TenantPublicIpItem[] {
    const kw = this.searchKeyword.trim().toLowerCase();
    if (!kw) {
      return this.listOfData;
    }
    return this.listOfData.filter(
      item =>
        (item.publicIp && item.publicIp.toLowerCase().includes(kw)) ||
        (item.remark && item.remark.toLowerCase().includes(kw)) ||
        (item.creatorName && item.creatorName.toLowerCase().includes(kw))
    );
  }

  loadData(): void {
    if (!this.tenantId) {
      this.listOfData = [];
      this.total = 0;
      this.refreshCheckStatus();
      return;
    }

    this.loading = true;
    this.http
      .post(ConstantUtil.TENANT_PUBLIC_IP_WHITELIST_LIST, {
        tenantId: this.tenantId,
        page: this.pageIndex,
        size: this.pageSize
      })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res?.success || res?.status === 0 || res?.code === 'OK' || res?.code === '0') {
            const pageData = res?.data;
            const rawList: any[] = pageData?.items ?? pageData?.records ?? pageData?.list ?? [];
            this.listOfData = rawList.map(item => ({
              ...item,
              publicIp: item.normalizedPublicIp || item.publicIp || ''
            }));
            this.total = pageData?.totalCount ?? pageData?.total ?? rawList.length;
          } else {
            this.listOfData = [];
            this.total = 0;
          }
          this.refreshCheckStatus();
        },
        error: () => {
          this.listOfData = [];
          this.total = 0;
          this.refreshCheckStatus();
        }
      });
  }

  onSearch(): void {
    // 支援前端搜尋過濾與重新加載
    this.refreshCheckStatus();
  }

  resetSearch(): void {
    this.searchKeyword = '';
    this.refreshCheckStatus();
  }

  pageIndexChange(index: number): void {
    this.pageIndex = index;
    this.loadData();
  }

  pageSizeChange(size: number): void {
    this.pageSize = size;
    this.pageIndex = 1;
    this.loadData();
  }

  onAllChecked(checked: boolean): void {
    this.listOfData.forEach(item => {
      if (checked) {
        this.selectedIds.add(item.id);
      } else {
        this.selectedIds.delete(item.id);
      }
    });
    this.refreshCheckStatus();
  }

  onItemChecked(id: string, checked: boolean): void {
    if (checked) {
      this.selectedIds.add(id);
    } else {
      this.selectedIds.delete(id);
    }
    this.refreshCheckStatus();
  }

  clearSelection(): void {
    this.selectedIds.clear();
    this.refreshCheckStatus();
  }

  refreshCheckStatus(): void {
    if (!this.listOfData.length) {
      this.isAllCurrentPageSelected = false;
      this.isIndeterminate = false;
      return;
    }
    const currentPageSelectedCount = this.listOfData.filter(item => this.selectedIds.has(item.id)).length;
    this.isAllCurrentPageSelected = currentPageSelectedCount === this.listOfData.length;
    this.isIndeterminate = currentPageSelectedCount > 0 && !this.isAllCurrentPageSelected;
  }

  openBatchAddModal(): void {
    this.batchAddForm.reset({ ips: '', remark: '' });
    this.batchAddVisible = true;
  }

  closeBatchAddModal(): void {
    this.batchAddVisible = false;
    this.batchAddForm.reset();
  }

  parseIps(raw: string): string[] {
    if (!raw) return [];
    return Array.from(
      new Set(
        raw
          .split(/[\r\n,;]+/)
          .map(ip => ip.trim())
          .filter(ip => !!ip)
      )
    );
  }

  isValidIpv4(ip: string): boolean {
    const ipv4Regex = /^(?:(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)\.){3}(?:25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)$/;
    const ipv6Regex =
      /^(([0-9a-fA-F]{1,4}:){7,7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|([0-9a-fA-F]{1,4}:){1,6}:[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|[0-9a-fA-F]{1,4}:((:[0-9a-fA-F]{1,4}){1,6})|:((:[0-9a-fA-F]{1,4}){1,7}|:)|fe80:(:[0-9a-fA-F]{0,4}){0,4}%[0-9a-zA-Z]{1,}|::(ffff(:0{1,4}){0,1}:){0,1}((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])|([0-9a-fA-F]{1,4}:){1,4}:((25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9])\.){3,3}(25[0-5]|(2[0-4]|1{0,1}[0-9]){0,1}[0-9]))$/;
    return ipv4Regex.test(ip) || ipv6Regex.test(ip);
  }

  get detectedIpCount(): number {
    const raw = this.batchAddForm?.get('ips')?.value || '';
    return this.parseIps(raw).length;
  }

  submitBatchAdd(): void {
    const rawIps = this.batchAddForm.get('ips')?.value || '';
    const ips = this.parseIps(rawIps);

    if (!ips.length) {
      this.message.error(this.i18n.fanyi('tenant.ip-range.modal.empty-error'));
      return;
    }

    const hasInvalid = ips.some(ip => !this.isValidIpv4(ip));
    if (hasInvalid) {
      this.message.error(this.i18n.fanyi('tenant.ip-range.modal.invalid-format'));
      return;
    }

    const remark = this.batchAddForm.get('remark')?.value?.trim() || null;
    this.batchAddLoading = true;

    this.http
      .post(ConstantUtil.TENANT_PUBLIC_IP_WHITELIST_BATCH_ADD, {
        tenantId: this.tenantId,
        ipList: ips,
        ips,
        remark
      })
      .pipe(
        finalize(() => {
          this.batchAddLoading = false;
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: res => {
          if (res?.success || res?.status === 0 || res?.code === 'OK') {
            this.message.success(this.i18n.fanyi('tenant.ip-range.add-success'));
            this.closeBatchAddModal();
            this.loadData();
          } else {
            this.message.error(res?.msg || 'Failed');
          }
        },
        error: err => {
          this.message.error(err?.message || 'Error');
        }
      });
  }

  deleteSingle(item: TenantPublicIpItem): void {
    this.http
      .post(ConstantUtil.TENANT_PUBLIC_IP_WHITELIST_DELETE, {
        tenantId: this.tenantId,
        ids: [item.id]
      })
      .subscribe({
        next: res => {
          if (res?.success || res?.status === 0 || res?.code === 'OK') {
            this.message.success(this.i18n.fanyi('tenant.ip-range.delete-success'));
            this.selectedIds.delete(item.id);
            this.refreshCheckStatus();
            this.loadData();
          } else {
            this.message.error(res?.msg || 'Failed');
          }
        },
        error: err => {
          this.message.error(err?.message || 'Error');
        }
      });
  }

  deleteBatch(): void {
    if (this.selectedIds.size === 0) return;
    const count = this.selectedIds.size;

    this.modal.confirm({
      nzTitle: this.i18n.fanyi('tenant.ip-range.delete-confirm-batch-title'),
      nzContent: this.i18n.fanyi('tenant.ip-range.delete-confirm-batch-content', { count }),
      nzOkText: this.i18n.fanyi('common.confirm'),
      nzCancelText: this.i18n.fanyi('common.cancel'),
      nzOkDanger: true,
      nzOnOk: () => {
        this.http
          .post(ConstantUtil.TENANT_PUBLIC_IP_WHITELIST_DELETE, {
            tenantId: this.tenantId,
            ids: Array.from(this.selectedIds)
          })
          .subscribe({
            next: res => {
              if (res?.success || res?.status === 0 || res?.code === 'OK') {
                this.message.success(this.i18n.fanyi('tenant.ip-range.delete-success'));
                this.selectedIds.clear();
                this.refreshCheckStatus();
                this.loadData();
              } else {
                this.message.error(res?.msg || 'Failed');
              }
            },
            error: err => {
              this.message.error(err?.message || 'Error');
            }
          });
      }
    });
  }

  trackByIpId(index: number, item: TenantPublicIpItem): string {
    return item.id;
  }
}
